import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { MarkdownRenderer } from '../Chat/MarkdownRenderer';

const ModalOverlay = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.98);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  max-width: 900px;
  width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(102, 126, 234, 0.1);
  border-radius: 20px 20px 0 0;
`;

const ReportTitle = styled.h2`
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:before {
    content: '📊';
    font-size: 1.8rem;
  }
`;

const CloseReportButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(248, 113, 113, 0.3);
    transform: rotate(90deg);
  }
`;

const ReportContent = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(85vh - 150px);
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.75;
  font-size: 1.05rem;

  /* Markdown styles */
  h1, h2, h3, h4 {
    color: #ffd700;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  h1 {
    font-size: 1.7rem;
    border-bottom: 2px solid rgba(102, 126, 234, 0.5);
    padding-bottom: 0.5rem;
  }

  h2 {
    font-size: 1.45rem;
  }

  h3 {
    font-size: 1.25rem;
  }

  h4 {
    font-size: 1.1rem;
  }

  p {
    margin: 0.75rem 0;
  }

  strong {
    color: #ffd700;
    font-weight: 600;
  }

  code {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.95em;
    color: #4ade80;
  }

  pre {
    background: rgba(0, 0, 0, 0.4);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1rem 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    line-height: 1.6;

    code {
      background: none;
      padding: 0;
      color: #d4d4d4;
      font-size: 1rem;
    }
  }

  ul, ol {
    margin-left: 1.5rem;
    margin: 0.75rem 0;
  }

  li {
    margin: 0.4rem 0;
  }

  blockquote {
    border-left: 4px solid #667eea;
    padding-left: 1rem;
    margin: 1rem 0;
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
  }
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  border-top: 3px solid #667eea;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StreamingCursor = styled.span`
  display: inline-block;
  margin-left: 2px;
  color: #667eea;
  animation: blink 1s infinite;
  font-weight: bold;

  @keyframes blink {
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
`;

interface AIReportPanelProps {
  report: string;
  isLoading: boolean;
  visible: boolean;
  onClose: () => void;
  isStreaming?: boolean;
}

export const AIReportPanel: React.FC<AIReportPanelProps> = ({
  report,
  isLoading,
  visible,
  onClose,
  isStreaming = false
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = React.useRef(true);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (visible && e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose]);

  // A new stream starts pinned to the bottom again.
  React.useEffect(() => {
    if (isStreaming) {
      pinnedToBottomRef.current = true;
    }
  }, [isStreaming]);

  // Follow the incoming text, but stop following once the user scrolls up to read.
  React.useEffect(() => {
    const el = contentRef.current;
    if (isStreaming && el && pinnedToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [report, isStreaming]);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  if (!visible) return null;

  return createPortal(
    <ModalOverlay $visible={visible} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ReportHeader>
          <ReportTitle>AI生成的实验案例</ReportTitle>
          <CloseReportButton onClick={onClose} title="关闭 (ESC)">
            ×
          </CloseReportButton>
        </ReportHeader>

        {isLoading && !report ? (
          <LoadingState>
            <LoadingSpinner />
            <div>AI正在生成实验案例，请稍候...</div>
          </LoadingState>
        ) : (
          <ReportContent ref={contentRef} onScroll={handleScroll}>
            <MarkdownRenderer content={report} fontSize="1.05rem" />
            {isStreaming && <StreamingCursor>▋</StreamingCursor>}
          </ReportContent>
        )}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
